import { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CircularProgress, Modal, TextField } from '@mui/material';
import { CloseSVG } from '@/components/Icon/Close';
import { DocumentsSVG } from '@/components/Icon/Documents';
import useCurrentLang from '@/hooks/useCurrentLang';
import * as S from '@/components/DocumentSharing/styles';
import {
  createDocumentShareInvitation, listDocumentShareInvitations, resendDocumentShareInvitation,
  revokeDocumentShareInvitation, invitationErrorKey, AccessMode, DocumentShareInvitation, ExternalShareLimits,
  ShareTargetType, InvitationErrorKey,
} from '@/api/documentShareInvitation.api';

/** Limits for external recipients, always rendered from backend values so the UI cannot drift from the server. */
export function ExternalLimitList({ limits }: { limits: ExternalShareLimits }) {
  // @ts-ignore i18next's deeply nested translation key type exceeds CRA's incremental compiler limit.
  const { t } = useTranslation();
  return <S.LimitList>
    <li>{t('documentEmailShare.limits.expiry', { days: limits.expiry_days })}</li>
    <li>{t('documentEmailShare.limits.code', { minutes: limits.code_minutes })}</li>
    <li>{t('documentEmailShare.limits.session', { minutes: limits.session_minutes })}</li>
    <li>{limits.download_limit ? t('documentEmailShare.limits.downloads', { count: limits.download_limit }) : t('documentEmailShare.limits.downloadsUnlimited')}</li>
    <li>{t('documentEmailShare.limits.scope')}</li>
    <li>{t('documentEmailShare.limits.revoke')}</li>
  </S.LimitList>;
}

interface Props { show: boolean; onClose: () => void; targetId: number; targetType: ShareTargetType; targetName?: string }
// Poll only while a delivery is still in the outbox, and only for a bounded window after opening/sending,
// because acceptance by the recipient has no predictable time and should not keep hitting the API.
export const EMAIL_SHARE_POLL_INTERVAL_MS = 5000;
export const EMAIL_SHARE_POLL_WINDOW_MS = 120000;
const isDeliveryInFlight = (item: DocumentShareInvitation) =>
  item.status === 'pending' && (item.delivery_status === 'queued' || item.delivery_status === 'sending');
export default function ShareToEmailModal({ show, onClose, targetId, targetType, targetName }: Props) {
  // @ts-ignore i18next's deeply nested translation key type exceeds CRA's incremental compiler limit.
  const { t } = useTranslation();
  const lang = useCurrentLang();
  const { control, handleSubmit, reset } = useForm<{ email: string }>({ defaultValues: { email: '' } });
  const [items, setItems] = useState<DocumentShareInvitation[]>([]);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<InvitationErrorKey | ''>('');
  const [notice, setNotice] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState<number | null>(null);
  const [external, setExternal] = useState<ExternalShareLimits | null>(null);
  const [accessMode, setAccessMode] = useState<AccessMode>('member');
  const [pendingExternalEmail, setPendingExternalEmail] = useState<string | null>(null);
  const externalAllowed = !!external?.enabled && targetType === 'document';
  const dateFormat = new Intl.DateTimeFormat(lang === 'en' ? 'en-GB' : 'zh-Hant', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  const requestVersion = useRef(0);
  const pollUntil = useRef(0);
  const extendPolling = () => { pollUntil.current = Date.now() + EMAIL_SHARE_POLL_WINDOW_MS; };
  // A silent refresh keeps the current list on screen instead of swapping it for the loading state.
  const load = useCallback(async (requestedPage = 1, silent = false) => {
    const version = ++requestVersion.current;
    if (!silent) setLoading(true);
    try {
      const { data } = await listDocumentShareInvitations(targetType, targetId, requestedPage);
      if (requestVersion.current !== version) return;
      setItems(data.data.items); setPage(data.data.current_page); setLastPage(data.data.last_page);
      setExternal(data.data.external ?? null);
    } catch (e) {
      if (requestVersion.current === version && !silent) setError(invitationErrorKey(e));
    } finally { if (requestVersion.current === version && !silent) setLoading(false); }
  }, [targetId, targetType]);
  useEffect(() => {
    if (!show) return;
    reset({ email: '' }); setItems([]); setError(''); setNotice(false); setPage(1); setConfirmRevoke(null);
    setAccessMode('member'); setPendingExternalEmail(null);
    extendPolling();
    void load();
    return () => { requestVersion.current++; };
  }, [show, load, reset]);
  useEffect(() => {
    if (!show || busy || loading || !items.some(isDeliveryInFlight) || Date.now() >= pollUntil.current) return undefined;
    const timer = window.setTimeout(() => { void load(page, true); }, EMAIL_SHARE_POLL_INTERVAL_MS);
    return () => window.clearTimeout(timer);
  }, [show, busy, loading, items, page, load]);
  const create = async (email: string, mode: AccessMode) => {
    if (busy) return;
    setBusy(true); setSending(true); setError(''); setNotice(false);
    try {
      await createDocumentShareInvitation({
        target_type: targetType, target_id: targetId, email, locale: ['tc', 'sc'].includes(lang) ? lang : 'en', access_mode: mode,
      });
      reset({ email: '' }); setPendingExternalEmail(null); setNotice(true); extendPolling(); await load();
    } catch (e) { setError(invitationErrorKey(e)); }
    finally { setBusy(false); setSending(false); }
  };
  // External access always needs an explicit confirmation before anything is sent.
  const send = handleSubmit(async ({ email }) => {
    if (busy) return;
    if (accessMode === 'external' && externalAllowed) { setError(''); setPendingExternalEmail(email.trim()); return; }
    await create(email.trim(), 'member');
  });
  const act = async (id: number, revoke: boolean) => {
    if (busy) return;
    setBusy(true); setError(''); setNotice(false);
    try {
      if (revoke) await revokeDocumentShareInvitation(id);
      else { await resendDocumentShareInvitation(id); extendPolling(); }
      setConfirmRevoke(null);
      await load(page);
    } catch (e) { setError(invitationErrorKey(e)); }
    finally { setBusy(false); }
  };
  return <Modal open={show} onClose={() => { if (!busy) onClose(); }}
    BackdropProps={{ sx: { backgroundColor: 'rgba(18, 30, 57, .46)' } }}>
    <S.Surface onSubmit={send} role="dialog" aria-modal="true" aria-labelledby="email-share-title" aria-describedby="email-share-description" tabIndex={-1}>
      <S.Header>
        <div style={{ flex: 1, minWidth: 0 }}>
          <S.Heading id="email-share-title">{t('documentEmailShare.title')}</S.Heading>
          <S.Description id="email-share-description">{t('documentEmailShare.description')}</S.Description>
        </div>
        <S.CloseButton type="button" aria-label={t('documentEmailShare.closeDialog')} disabled={busy} onClick={onClose}>
          <CloseSVG aria-hidden="true" />
        </S.CloseButton>
      </S.Header>
      <S.ScrollBody>
        {targetName && <S.Target>
          <S.FileIcon aria-hidden="true"><DocumentsSVG /></S.FileIcon>
          <div style={{ minWidth: 0 }}><small>{t(`documentEmailShare.target.${targetType}`)}</small><strong>{targetName}</strong></div>
        </S.Target>}
        <S.ComposeRow>
          <Controller control={control} name="email" rules={{
            required: String(t('documentEmailShare.emailInvalid')),
            validate: value => (value.trim().length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) || String(t('documentEmailShare.emailInvalid')),
          }} render={({ field: { ref, ...field }, fieldState }) => <TextField {...field} inputRef={ref} id="share-recipient-email" type="email" fullWidth
            label={t('documentEmailShare.recipientLabel')} disabled={busy} autoComplete="off"
            error={!!fieldState.error} helperText={fieldState.error?.message}
            inputProps={{ maxLength: 254, inputMode: 'email', spellCheck: false, autoCapitalize: 'none' }} />} />
          <S.PrimaryButton type="submit" disabled={busy} aria-busy={sending}>
            {sending && <CircularProgress size={18} aria-hidden="true" />}{t('documentEmailShare.send')}
          </S.PrimaryButton>
        </S.ComposeRow>
        {external?.enabled ? <S.ModeGroup disabled={busy}>
          <legend>{t('documentEmailShare.modeLegend')}</legend>
          <S.ModeOption>
            <input type="radio" name="share-access-mode" value="member" checked={accessMode === 'member'}
              onChange={() => { setAccessMode('member'); setPendingExternalEmail(null); }} />
            <div><strong>{t('documentEmailShare.modeMember')}</strong><span>{t('documentEmailShare.modeMemberHint', { days: external.member_expiry_days })}</span></div>
          </S.ModeOption>
          <S.ModeOption $disabled={!externalAllowed}>
            <input type="radio" name="share-access-mode" value="external" checked={accessMode === 'external'} disabled={!externalAllowed}
              aria-describedby="share-external-hint" onChange={() => setAccessMode('external')} />
            <div><strong>{t('documentEmailShare.modeExternal')}</strong>
              <span id="share-external-hint">{t(externalAllowed ? 'documentEmailShare.modeExternalHint' : 'documentEmailShare.externalFolderUnavailable')}</span></div>
          </S.ModeOption>
          {accessMode === 'external' && externalAllowed && <section aria-label={String(t('documentEmailShare.limitsTitle'))}>
            <ExternalLimitList limits={external} />
          </section>}
        </S.ModeGroup> : <S.Hint>{t('documentEmailShare.inviteHint')}</S.Hint>}
        {pendingExternalEmail && external && <S.ConfirmArea role="group" aria-labelledby="external-confirm-title" aria-describedby="external-confirm-body">
          <p id="external-confirm-title"><strong>{t('documentEmailShare.confirmTitle')}</strong></p>
          <p id="external-confirm-body">{t('documentEmailShare.confirmBody', { email: pendingExternalEmail, name: targetName || t('documentEmailShare.sharedItem') })}</p>
          <ExternalLimitList limits={external} />
          <S.RowActions style={{ marginTop: 12 }}>
            <S.DangerButton type="button" disabled={busy} aria-busy={sending} onClick={() => void create(pendingExternalEmail, 'external')}>
              {sending && <CircularProgress size={18} aria-hidden="true" />}{t('documentEmailShare.confirmSend')}
            </S.DangerButton>
            <S.QuietButton type="button" disabled={busy} onClick={() => setPendingExternalEmail(null)}>{t('buttons.cancel')}</S.QuietButton>
          </S.RowActions>
        </S.ConfirmArea>}
        {error && <S.Feedback $error role="alert">{t(`documentEmailShare.errors.${error}`)}</S.Feedback>}
        {notice && <S.Feedback role="status" aria-live="polite">{t('documentEmailShare.saved')}</S.Feedback>}
        <S.ListSection aria-labelledby="email-invitation-list-title" aria-busy={loading}>
          <S.SectionHeading>
            <h3 id="email-invitation-list-title">{t('documentEmailShare.invitations')}</h3>
            <S.QuietButton type="button" disabled={busy || loading} onClick={() => { setError(''); void load(page); }}>{t('documentEmailShare.refresh')}</S.QuietButton>
          </S.SectionHeading>
          {loading ? <S.EmptyState role="status">{t('documentEmailShare.loading')}</S.EmptyState> : items.length === 0 ? <S.EmptyState>
            <p>{t('documentEmailShare.empty')}</p><small>{t('documentEmailShare.emptyHint')}</small>
          </S.EmptyState> : <S.InvitationList>{items.map(item => <S.InvitationRow key={item.id}>
            <S.RowLayout>
              <div style={{ minWidth: 0 }}>
                <S.Recipient>{item.email}</S.Recipient>
                <S.RowMeta>
                  {item.access_mode === 'external' && <span>{t('documentEmailShare.mode.external')}</span>}
                  <S.Status $status={item.status}>{t(`documentEmailShare.status.${item.status}`)}</S.Status><span>{t(`documentEmailShare.delivery.${item.delivery_status}`)}</span>
                </S.RowMeta>
                <S.RowMeta><span>{t('documentEmailShare.expires')} <time dateTime={item.expires_at}>{dateFormat.format(new Date(item.expires_at))}</time></span></S.RowMeta>
                {item.access_mode === 'external' && <S.RowMeta>
                  <span>{item.download_limit
                    ? t('documentEmailShare.downloadsUsed', { count: item.download_count ?? 0, limit: item.download_limit })
                    : t('documentEmailShare.downloadsUsedUnlimited', { count: item.download_count ?? 0 })}</span>
                  {item.last_accessed_at && <span>{t('documentEmailShare.lastAccessed')} <time dateTime={item.last_accessed_at}>{dateFormat.format(new Date(item.last_accessed_at))}</time></span>}
                </S.RowMeta>}
              </div>
              <S.RowActions>
                {item.status !== 'accepted' && <S.QuietButton type="button" disabled={busy} onClick={() => void act(item.id, false)}>{t('documentEmailShare.resend')}</S.QuietButton>}
                {item.status !== 'revoked' && <S.DangerButton type="button" disabled={busy} onClick={() => setConfirmRevoke(item.id)} aria-expanded={confirmRevoke === item.id}>{t('documentEmailShare.revoke')}</S.DangerButton>}
              </S.RowActions>
            </S.RowLayout>
            {confirmRevoke === item.id && <S.ConfirmArea>
              <p>{t('documentEmailShare.revokeConfirm')}</p>
              <S.RowActions>
                <S.DangerButton type="button" disabled={busy} onClick={() => void act(item.id, true)}>{t('documentEmailShare.confirmRevoke')}</S.DangerButton>
                <S.QuietButton type="button" disabled={busy} onClick={() => setConfirmRevoke(null)}>{t('buttons.cancel')}</S.QuietButton>
              </S.RowActions>
            </S.ConfirmArea>}
          </S.InvitationRow>)}</S.InvitationList>}
          {lastPage > 1 && <S.Pagination aria-label={t('documentEmailShare.invitations')}>
            <S.QuietButton type="button" disabled={busy || loading || page <= 1} onClick={() => void load(page - 1)}>{t('documentEmailShare.previous')}</S.QuietButton>
            <span>{page} / {lastPage}</span>
            <S.QuietButton type="button" disabled={busy || loading || page >= lastPage} onClick={() => void load(page + 1)}>{t('documentEmailShare.next')}</S.QuietButton>
          </S.Pagination>}
        </S.ListSection>
      </S.ScrollBody>
      <S.Footer><p>{t('documentEmailShare.readOnlyNote')}</p><S.SecondaryButton type="button" disabled={busy} onClick={onClose}>{t('buttons.close', 'Close')}</S.SecondaryButton></S.Footer>
    </S.Surface>
  </Modal>;
}
